import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { LineChart, Brain } from "lucide-react";

const QUOTES = [
  {
    line1: "Most traders don't fail",
    line2: "because they're bad.",
    line3: "They fail because they doubt",
    line4: "the plan they just made.",
  },
  {
    line1: "Markets don't",
    line2: "break traders.",
    line3: "Emotions do.",
    line4: "Every. Single. Time.",
  },
  {
    line1: "Dopamine wins —",
    line2: "then dopamine",
    line3: "loses it all",
    line4: "right back.",
  },
  {
    line1: "The bot has",
    line2: "no doubt.",
    line3: "No fear. No FOMO.",
    line4: "Just the plan.",
  },
  {
    line1: "You don't lose",
    line2: "to the market.",
    line3: "You lose to",
    line4: "yourself.",
  },
  {
    line1: "A perfect setup",
    line2: "means nothing",
    line3: "if your hand shakes",
    line4: "on the trigger.",
  },
  {
    line1: "Discipline is what",
    line2: "bots do for free.",
    line3: "Traders pay for it",
    line4: "in losses.",
  },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Rotating quote carousel
  const [quoteIdx, setQuoteIdx] = useState(0);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const id = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setQuoteIdx((i) => (i + 1) % QUOTES.length);
        setFading(false);
      }, 400);
    }, 5200);
    return () => clearInterval(id);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u?.role === "admin" ? "/admin" : "/app");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const q = QUOTES[quoteIdx];

  return (
    <div className="min-h-screen auth-bg flex">
      <div className="w-full md:w-[480px] flex flex-col px-6 sm:px-10 py-12 border-r border-white/10 bg-[#09090B]/80 backdrop-blur-sm relative">
        <div className="flex items-center gap-2 mb-10">
          <LineChart className="w-5 h-5 text-teal-400" strokeWidth={1.5} />
          <div className="font-mono text-sm tracking-widest uppercase">BonBot</div>
        </div>

        <div className="my-auto">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Login</div>
          <h1 className="text-3xl font-medium tracking-tight mb-2">Welcome back.</h1>
          <p className="text-zinc-500 text-sm mb-8">Sign in to your trading console.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1 bg-black border border-white/10 rounded-sm px-3 py-2 text-sm font-mono focus:border-zinc-500 focus:outline-none"
                placeholder="trader@example.com"
                autoComplete="email"
                required
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-1 bg-black border border-white/10 rounded-sm px-3 py-2 text-sm font-mono focus:border-zinc-500 focus:outline-none"
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
            </div>
            {err && <div className="text-rose-400 text-sm font-mono" data-testid="login-error">{err}</div>}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-zinc-100 text-zinc-900 hover:bg-white py-2.5 rounded-sm text-sm font-medium transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-sm text-zinc-500">
            No account?{" "}
            <Link to="/register" className="text-teal-400 hover:text-teal-300" data-testid="link-register">
              Create one
            </Link>
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-10">
          Universal MT4 / MT5 broker support · Real-money capable
        </div>
      </div>

      {/* Right panel — animated trading-truth quotes */}
      <div className="hidden md:flex flex-1 items-center justify-center p-10 relative overflow-hidden">
        {/* Ambient background blobs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-teal-400/[0.05] rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] bg-cyan-400/[0.04] rounded-full blur-[120px]" />
        </div>

        <div className="max-w-xl w-full relative">
          {/* Brand line */}
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-teal-400 mb-8">
            <Brain className="w-3.5 h-3.5" strokeWidth={1.5} />
            // why most traders fail
          </div>

          {/* Flipping quote */}
          <div className="relative min-h-[280px]" data-testid="login-quote-block">
            <blockquote
              key={quoteIdx}
              className={`transition-all duration-500 ease-out ${fading ? "opacity-0 -translate-y-3" : "opacity-100 translate-y-0"}`}
            >
              <div className="text-3xl lg:text-4xl xl:text-5xl font-medium tracking-tight leading-[1.15] text-zinc-100">
                <span className="block">{q.line1}</span>
                <span className="block">{q.line2}</span>
                <span className="block bg-gradient-to-r from-teal-300 to-cyan-400 bg-clip-text text-transparent">{q.line3}</span>
                <span className="block">{q.line4}</span>
              </div>
            </blockquote>
          </div>

          {/* Footer reveal — always present */}
          <div className="mt-10 pt-6 border-t border-white/10">
            <div className="text-zinc-400 text-sm leading-relaxed max-w-lg">
              Your setup isn't broken. Your hand is.
              <br />
              <span className="text-teal-400">The bot doesn't doubt. Doesn't chase. Doesn't blink.</span>
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mt-8">
            {QUOTES.map((_, i) => (
              <span
                key={i}
                className={`h-px transition-all duration-500 ${i === quoteIdx ? "w-10 bg-teal-400" : "w-4 bg-white/15"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
